import { API_URL } from '../../Settings';

import GamePackageSender from './GamePackageSender';


const CSRF_COOKIE_NAME = 'csrftoken';
const CSRF_HEADER_NAME = 'X-CSRFToken';


function _readCookie(name) {
    if (typeof document === 'undefined' || !document.cookie) {
        return null;
    }
    const prefix = name + '=';
    const parts = document.cookie.split(';');
    for (let part of parts) {
        part = part.trim();
        if (part.indexOf(prefix) === 0) {
            return decodeURIComponent(part.substring(prefix.length));
        }
    }
    return null;
}


export default class GameAPI {

    constructor(game) {
        this.game = game;
        this.packageSender = new GamePackageSender(this);
    }

    claimLobbySeat = async (lobbyId, seatIndex, platformDisplayName) => {
        // Phase C: pre-launch seat claim via REST. Replaces the
        // legacy Group/Configure options.seat dual-write that died
        // with the options JSON migration.
        //
        // platformDisplayName is the Discord per-server nick (or
        // global_name / username fallback). Sent only when truthy so
        // the open-web body stays minimal and the backend falls
        // through to its account-level rendering path.
        const csrfToken = _readCookie(CSRF_COOKIE_NAME);
        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        };
        if (csrfToken) {
            headers[CSRF_HEADER_NAME] = csrfToken;
        }
        const body = { seat_index: seatIndex };
        if (platformDisplayName) {
            body.platform_display_name = platformDisplayName;
        }
        try {
            const response = await fetch(
                `${API_URL}lobby/${lobbyId}/claim_seat/`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers,
                    body: JSON.stringify(body),
                });
            return response.ok ? await response.json() : null;
        } catch (error) {
            return null;
        }
    }

    getLibrary = (libraryType) => {
        const typeName = 'Library';
        const packageValue = {
            type: libraryType,
            data: {
                type: libraryType
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    getGame = (gameId) => {
        const typeName = 'GetGame';
        const packageValue = {
            type: 'GetGame',
            data: {
                type: 'get',
                game_id: gameId
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    resumeGame = (gameId) => {
        this.resuming = true;  // this is because it needs to do sit after? if right seat?
        const typeName = 'Resume';
        const packageValue = {};
        if (gameId !== undefined && gameId !== null) {
            packageValue.data = { type: 'resume', game_id: gameId };
            // When resuming from inside a Discord Activity, carry the
            // channel binding so the engine can re-home the boot lobby
            // onto this game (CC_DISCORD_REJOIN_ROUTING). DiscordProvider
            // stashes it on the App at handshake time. Absent on open
            // web → the bare {type, game_id} contract is preserved.
            const binding = this.game && this.game.app
                && this.game.app.discordChannelBinding;
            if (binding) {
                packageValue.data.discord_channel_id =
                    binding.channel_id || '';
                packageValue.data.discord_guild_id = binding.guild_id || '';
                packageValue.data.discord_instance_id =
                    binding.instance_id || '';
            }
        }
        this.packageSender.sendPackage(typeName, packageValue);
    }

    listGames = () => {
        // CC_SAVED_GAMES_LIST (2026-05-27): pull the user's
        // {games_list, joined_games, open_games} buckets via the
        // existing 'List' backend route (engine/message_router.py:38).
        const typeName = 'List';
        const packageValue = {};
        this.packageSender.sendPackage(typeName, packageValue);
    }

    subscribeFromPlatformChannel = (channelId) => {
        // CC_DISCORD_LOBBY_LIFECYCLE §5: join this connection to the
        // channel's from_platform group so the backend can push
        // `from_platform.changed` when a peer creates / joins / leaves /
        // launches a lobby in this Discord voice channel. The frontend
        // re-fetches the channel list on receipt. Routed through the
        // 'ChannelSubscribe' backend route (engine/message_router.py);
        // SocketHandler queues the send until the socket is open, so
        // this is race-safe against the cold-start handshake.
        if (!channelId) return;
        const typeName = 'ChannelSubscribe';
        const packageValue = {
            type: 'ChannelSubscribe',
            data: { type: 'subscribe', channel_id: channelId },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    nameGame = (gameId, name) => {
        // CC_GAME_NAMING: owner-only rename. Routes through the 'Name'
        // package → engine/controls/rename_game.py, which reads
        // received_message.package.data.name. Fire-and-forget; the
        // server-pushed game payload updates the visible name on the
        // next event.
        const typeName = 'Name';
        const packageValue = {
            type: 'Name',
            data: {
                type: 'name',
                game_id: gameId,
                name: name,
            },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    sit = (seat) => {
        const typeName = 'Sit';
        const packageValue = {
            type: 'Sit',
            data: {
                type: 'seat',
                seat: seat
            }
        };
        // give the game a moment to load.
        setTimeout(() => {
            this.packageSender.sendPackage(typeName, packageValue);
        }, 1000);
    }

    createNewGamePlayer = (seat) => {
        // Claim an empty seat on an existing Game by creating a new
        // GamePlayer (backend 'Join' -> create_game_player). Migrated
        // off GameController, which is null in production (App.js sets
        // gameController = null and never writes it), so the legacy
        // gameController.createNewGamePlayer reader silently fell
        // through to sit() and the backend raised
        // GamePlayer.DoesNotExist. Mirrors the wire shape sit() uses.
        const typeName = 'Join';
        const packageValue = {
            type: 'Join',
            data: {
                type: 'seat',
                seat: seat
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    getSimulation = () => {
        const typeName = 'Simulation';
        const packageValue = {
            type: 'Simulation',
            data: {
                type: 'simulation'
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    delete = () => {
        let typeName = 'Delete';
        let packageValue = {
            type: 'Delete',
            data: {game_id: this.game.gameData.id}
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    toggleActivation = (roomIndex) => {
        const typeName = 'Robot';
        const packageValue = {
            type: 'Robot',
            data: {
                type: 'Station',
                station_grid_index: roomIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    disconnect = () => {
        let typeName = 'Disconnect';
        let packageValue = {
            type: 'Disconnect',
            data: {
                type: 'disconnect'
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    fetchGroup = () => {
        let typeName = 'Group';
        let packageValue = { type: 'Members' };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    leaveGroup = () => {
        console.log('LEAVE GROUP')
        let typeName = 'Group';
        let packageValue = { type: 'Leave', data: { type: 'leave' } };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    endGameSession = () => {
        // Soft post-game exit: backend clears the connection's
        // game/game_player but PRESERVES the lobby/seat binding and
        // flips Lobby COMPLETED→FORMING on the first dismisser. The
        // explicit lobby-exit transport is `leaveGroup`; the two
        // must not be conflated.
        console.log('[postgame-fp] GameAPI.endGameSession sending WS EndGameSession');
        let typeName = 'Group';
        let packageValue = {
            type: 'EndGameSession',
            data: { type: 'end_game_session' },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    kickFromGroup = (userId) => {
        console.log('KICK FROM GROUP user=%s', userId);
        let typeName = 'Group';
        let packageValue = {
            type: 'Kick',
            data: { type: 'kick', user: userId },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    joinGroup = (group) => {
        console.log('JOIN GROUP', group);
        let typeName = 'Group';
        let packageValue = { type: 'Join', data: {
            type: 'join', group: group }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    addArtificialPlayer = (factionId) => {
        // Post CC_LOBBY: factionId is optional. When absent the AI
        // lands in owner.options.ai[] with hotel=null and the in-game
        // CHOOSE_FACTION_ID ceremony picks a faction at launch.
        let typeName = 'Group';
        let data = {
            type: 'addai',
            ai: 3,
        };
        if (factionId !== null && factionId !== undefined) {
            data.hotel = factionId;
        }
        let packageValue = {
            type: 'AddAI',
            data: data,
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    removeArtificialPlayer = (target) => {
        // Two payload shapes accepted by the engine (per CC_LOBBY §4.2):
        //   - {index: N} — preferred, disambiguates null-hotel AIs.
        //   - factionId (int) — legacy match-by-faction; kept for
        //     pre-CC_LOBBY callers.
        let typeName = 'Group';
        let data = { type: 'removeai' };
        if (target !== null && target !== undefined
                && typeof target === 'object' && 'index' in target) {
            data.index = target.index;
        } else if (target !== null && target !== undefined) {
            data.hotel = target;
        }
        let packageValue = {
            type: 'RemoveAI',
            data: data,
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    triggerSinglePlayer = () => {
        // Owner-only auto-fill: backend appends 5 null-hotel AIs to
        // owner.options.ai[]. Non-owner senders are rejected server-side.
        let typeName = 'Group';
        let packageValue = {
            type: 'SinglePlayer',
            data: { type: 'singleplayer' },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    configureGroupOptions = (options) => {
        let typeName = 'Group';
        let packageValue = {
            type: 'Configure',
            data:{
                type:'configure',
                options: options
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    setSeatClosed = (seatNumber, closed) => {
        // CC_CLOSED_SEAT_BROADCAST: closing a seat is owner-only,
        // server-owned lobby state. Mirrors the AddAI message family —
        // the backend close_seat handler owner-gates, reserves/frees
        // the Seat.closed flag, and re-broadcasts Members so every
        // client renders the same shut seats (the old FE-local Set is
        // gone). Non-owner sends are rejected server-side.
        let typeName = 'Group';
        let packageValue = {
            type: 'CloseSeat',
            data: {
                type: 'closeseat',
                seat: seatNumber,
                closed: closed,
            },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    saveGame = () => {
        // CC_LOBBY_GAME_CRUD (2026-05-27): owner-only Leave Game
        // (relabeled from Save & Exit by CC_GAME_NAMING; games autosave,
        // so the action is just leave). Backend flips Game.status to
        // GameStatus.SAVING_ID and releases every connection to AT_MENU.
        // CC_PATIENT_SEAT_MULTIPLAYER (2026-06-01): the Lobby row is
        // PRESERVED (status stays PLAYING, lobby.game stays set) so the
        // game is resumable from /saves; peers receive
        // LobbyDestroyed(reason='saved'). The old "lobby is destroyed"
        // behavior is gone.
        console.log('SAVE GAME');
        const typeName = 'Group';
        const packageValue = {
            type: 'SaveGame',
            data: { type: 'save_game' },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    abandonGame = (gameId) => {
        // CC_LOBBY_GAME_CRUD (2026-05-27): owner-only Abandon.
        // Backend delete_game expects `data.game_id` (see
        // engine/controls/delete_game.py:16); wire format mirrors the
        // existing `delete` method on this class for consistency.
        console.log('ABANDON GAME id=%s', gameId);
        const typeName = 'Delete';
        const packageValue = {
            type: 'Delete',
            data: { game_id: gameId },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    replaceSeatWithAI = (seat, aiId) => {
        // CC_LOBBY_GAME_CRUD (2026-05-27): owner-only seat replacement.
        // ai_id is optional; backend falls back to the default
        // SinglePlayer AI when omitted.
        console.log('REPLACE SEAT WITH AI seat=%s ai=%s', seat, aiId);
        const typeName = 'Group';
        const packageValue = {
            type: 'ReplaceSeatWithAI',
            data: {
                type: 'replace_seat_with_ai',
                seat: seat,
            },
        };
        if (aiId !== undefined && aiId !== null) {
            packageValue.data.ai_id = aiId;
        }
        this.packageSender.sendPackage(typeName, packageValue);
    };

    // CC_REQUEST_A_SEAT (2026-06-08): the PRIVATE-game recruitment rail.
    // An observer of a private game requests a recruitable AI seat; the
    // owner approves/declines. All four ride the existing Group rail
    // (mirror ReplaceSeatWithAI) — no new message family.
    requestSeat = (seat) => {
        const typeName = 'Group';
        const packageValue = {
            type: 'RequestSeat',
            data: { type: 'request_seat', seat: seat },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    approveSeatRequest = (userId, seat) => {
        const typeName = 'Group';
        const packageValue = {
            type: 'ApproveSeatRequest',
            data: { type: 'approve_seat_request', user_id: userId, seat: seat },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    declineSeatRequest = (userId, seat) => {
        const typeName = 'Group';
        const packageValue = {
            type: 'DeclineSeatRequest',
            data: { type: 'decline_seat_request', user_id: userId, seat: seat },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    cancelSeatRequest = () => {
        const typeName = 'Group';
        const packageValue = {
            type: 'CancelSeatRequest',
            data: { type: 'cancel_seat_request' },
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };

    sendLaunchPackage = (opts = {}) => {
        // CC_PLAYER_ONE_TRANSFER: owner-designated Player One seat
        // rides on the Launch payload. Engine validates and clamps;
        // missing/invalid values default to seat 0 server-side, but
        // we still send a clean integer here.
        let typeName = 'Group';
        let packageValue = {
            type: 'Launch',
            data:{
                type:'launch',
                player_one_seat: Number.isInteger(opts.player_one_seat)
                    ? opts.player_one_seat : 0,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    };
}
